import React, { useState, useEffect } from "react";
import { useAuth } from "../AuthContext";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ChangePassword from "./ChangePassword";
import TestingModal from "./modules/TestingModal";

// 🔔 Popup utility
const showCenteredPopup = (message, type = "info") => {
    const popup = document.createElement("div");
    popup.innerText = message;
    popup.style.position = "fixed";
    popup.style.top = "50%";
    popup.style.left = "50%";
    popup.style.transform = "translate(-50%, -50%)";
    popup.style.padding = "16px 24px";
    popup.style.borderRadius = "12px";
    popup.style.fontWeight = "600";
    popup.style.color = "#fff";
    popup.style.fontSize = "16px";
    popup.style.textAlign = "center";
    popup.style.zIndex = "9999";
    popup.style.boxShadow = "0 4px 10px rgba(0,0,0,0.3)";
    popup.style.opacity = "0";
    popup.style.transition = "opacity 0.4s ease, transform 0.4s ease";

    // 🟩 Color based on type
    popup.style.background =
        type === "success"
            ? "linear-gradient(135deg, #22c55e, #16a34a)" // green
            : type === "error"
                ? "linear-gradient(135deg, #dc2626, #b91c1c)" // red
                : "linear-gradient(135deg, #3b82f6, #2563eb)"; // blue (info)

    document.body.appendChild(popup);
    setTimeout(() => {
        popup.style.opacity = "1";
        popup.style.transform = "translate(-50%, -50%) scale(1.05)";
    }, 10);

    setTimeout(() => {
        popup.style.opacity = "0";
        popup.style.transform = "translate(-50%, -50%) scale(0.9)";
        setTimeout(() => document.body.removeChild(popup), 400);
    }, 2500);
};


const DeveloperDashboard = () => {
    const { username, logout } = useAuth();
    const [taskType, setTaskType] = useState("");
    const [projectName, setProjectName] = useState("");
    const [date, setDate] = useState(null);
    const [timeSlot, setTimeSlot] = useState("");
    const [description, setDescription] = useState("");
    const [documentFile, setDocumentFile] = useState(null);
    const [status, setStatus] = useState("In Progress");
    const [tasks, setTasks] = useState([]);
    const [backlogTasks, setBacklogTasks] = useState([]);
    const [selectedBacklogTask, setSelectedBacklogTask] = useState(null);
    const [error, setError] = useState(null);
    const [developerId, setDeveloperId] = useState(null);
    const [showChangePassword, setShowChangePassword] = useState(false);

    const [showCompleted, setShowCompleted] = useState(false);

    // Testing modal visibility state
    const [showTestingModal, setShowTestingModal] = useState(false);

    // Pagination states
    const [currentTaskPage, setCurrentTaskPage] = useState(1);
    const tasksPerPage = 10;

    const [currentBacklogPage, setCurrentBacklogPage] = useState(1);
    const backlogsPerPage = 5;

    // Drawer Menu State
    const [drawerOpen, setDrawerOpen] = useState(false)

    const BASE_URL = process.env.REACT_APP_BACKEND_URL;


    // ---------------------- FETCH TASKS ----------------------
    const fetchTasks = async (user = username) => {
        if (!user || user === "undefined") {
            console.warn("⚠️ Username not ready yet, skipping fetchTasks");
            return;
        }

        try {
            const res = await fetch(`${BASE_URL}/developer/tasks/${user}`);
            if (!res.ok) throw new Error("Failed to fetch tasks");

            const data = await res.json();
            const sortedTasks = data.sort((a, b) => new Date(b.date) - new Date(a.date));
            setTasks(sortedTasks);
        } catch (err) {
            console.error("Error fetching tasks:", err);
            setError("Failed to fetch tasks from server");
        }
    };

    // ---------------------- FETCH BACKLOG TASKS ----------------------
    const fetchBacklogs = async () => {
        if (!developerId) return;
        try {
            const res = await fetch(`${BASE_URL}/developer/backlogs/${developerId}`);
            const data = await res.json();
            setBacklogTasks(data);
        } catch (err) {
            console.error("Error fetching backlog tasks:", err);
        }
    };

    // ---------------------- LOAD DEVELOPER DATA ----------------------
    useEffect(() => {
        const loadDeveloperData = async () => {
            if (!username || username === "undefined") {
                console.warn("⏳ Username not ready yet, skipping data load");
                return;
            }

            try {
                // 🧩 1. Fetch developer info
                const res = await fetch(`${BASE_URL}/developer/info/${username}`);
                if (!res.ok) throw new Error("Failed to fetch developer info");
                const data = await res.json();
                setDeveloperId(data.id);

                // 🧩 2. Fetch developer tasks
                await fetchTasks(username);
                // ❌ Don’t call fetchBacklogs() here — it depends on developerId
            } catch (err) {
                console.error("❌ Error loading developer data:", err);
                setError("Unable to load developer data");
            }
        };

        loadDeveloperData();
    }, [username]);

    // Fetch backlog tasks when developerId is available
    useEffect(() => {
        if (developerId) {
            fetchBacklogs();
        }
    }, [developerId]);

    // ---------------------- ACKNOWLEDGE BACKLOG ----------------------
    const handleAcknowledge = async (task) => {
        try {
            const res = await fetch(`${BASE_URL}/developer/acknowledge/${task.id}`, {
                method: "PATCH",
            });
            if (!res.ok) {
                console.error("Failed to acknowledge backlog task");
                return;
            }
            const acknowledgedTask = await res.json();

            // Auto-fill form with acknowledged task
            setProjectName(acknowledgedTask.project_name);
            setDescription(acknowledgedTask.description);
            setSelectedBacklogTask(task);

            // Refresh backlog
            fetchBacklogs();
        } catch (err) {
            console.error("Error acknowledging backlog task:", err);
        }
    };

    // ---------------------- SUBMIT NEW TASK ----------------------
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        const correctedDate =
            date
                ? new Date(date.getTime() - date.getTimezoneOffset() * 60000)
                    .toISOString()
                    .split("T")[0]
                : "";

        const newTask = {
            developer_id: developerId,
            developer_name: username,
            project_name: projectName,
            task_type: taskType,
            date: correctedDate,
            time_slot: timeSlot,
            description,
            status,
        };

        try {
            const formData = new FormData();
            Object.entries(newTask).forEach(([key, value]) => {
                formData.append(key, value);
            });
            if (documentFile) formData.append("document", documentFile);

            const res = await fetch(`${BASE_URL}/developer/add-task`, {
                method: "POST",
                body: formData,
            });

            if (!res.ok) {
                const errData = await res.json();
                let errorMessage = "Failed to save task";
                if (Array.isArray(errData.detail)) {
                    errorMessage = errData.detail.map((err) => err.msg).join(", ");
                } else if (typeof errData.detail === "string") {
                    errorMessage = errData.detail;
                }
                setError(errorMessage);

                // ❌ Show failure popup
                showCenteredPopup(`❌ ${errorMessage}`, "error");
                return;
            }

            const savedTask = await res.json();
            setTasks((prev) => [savedTask, ...prev]);

            // ✅ Show success popup
            showCenteredPopup("✅ Task successfully added!", "success");

            // Reset form
            setTaskType("");
            setProjectName("");
            setDate(null);
            setTimeSlot("");
            setDescription("");
            setStatus("In Progress");
            setSelectedBacklogTask(null);
            setDocumentFile(null);

            // Refresh backlog tasks
            fetchBacklogs();
        } catch (err) {
            console.error("Error submitting task:", err);
            setError("Failed to submit task to server");
        }
    };

    // ---------------------- LOGOUT ----------------------
    const handleLogout = () => {
        logout();
        window.location.href = "/";
    };


    // Developer Tasks
    // ✅ Filter tasks first based on the toggle
    const filteredTasks = tasks.filter((task) =>
        showCompleted ? task.status === "Completed" : task.status === "In Progress"
    );

    // ✅ Then calculate pagination using only filtered tasks
    const totalTaskPages = Math.ceil(filteredTasks.length / tasksPerPage);
    const indexOfLastTask = currentTaskPage * tasksPerPage;
    const indexOfFirstTask = indexOfLastTask - tasksPerPage;
    const currentTasks = filteredTasks.slice(indexOfFirstTask, indexOfLastTask);

    // Backlog Tasks
    const indexOfLastBacklog = currentBacklogPage * backlogsPerPage;
    const indexOfFirstBacklog = indexOfLastBacklog - backlogsPerPage;
    const currentBacklogs = backlogTasks.slice(indexOfFirstBacklog, indexOfLastBacklog);
    const totalBacklogPages = Math.ceil(backlogTasks.length / backlogsPerPage);

    useEffect(() => {
        setCurrentTaskPage(1);
    }, [tasks]);

    useEffect(() => {
        setCurrentBacklogPage(1);
    }, [backlogTasks]);

    const handleStatusChange = async (taskId, currentStatus) => {
        try {
            // If already completed, do nothing
            if (currentStatus === "Completed") return;

            const newStatus = "Completed"; // one-way only

            const res = await fetch(`${BASE_URL}/developer/task-status/${taskId}?status=${newStatus}`, {
                method: "PUT",
            });

            if (!res.ok) throw new Error("Failed to update task status");
            const data = await res.json();

            // Update task status locally in state
            setTasks((prevTasks) =>
                prevTasks.map((t) => (t.id === taskId ? { ...t, status: newStatus } : t))
            );

            showCenteredPopup(`✅ ${data.message}`, "success");
        } catch (err) {
            console.error("Error updating task status:", err);
            showCenteredPopup("❌ Failed to update status", "error");
        }
    };

    return (
        <div className="min-h-screen bg-gray-900 text-white p-8">
            {/* ---------------- HEADER WITH HAMBURGER ---------------- */}
            <header className="flex justify-between items-center mb-8 bg-gray-900 text-white">
                <h1 className="text-3xl font-bold">
                    Developer Dashboard - Welcome,{" "}
                    <span className="text-indigo-400">{username}</span> 👋
                </h1>

                {/* HAMBURGER BUTTON */}
                <button
                    onClick={() => setDrawerOpen(true)}
                    className="bg-gray-800 hover:bg-gray-700 px-4 py-2 rounded-md text-lg"
                >
                    ☰
                </button>
            </header>

            {/* ---------------- SIDE DRAWER BACKDROP ---------------- */}
            {drawerOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40"
                    onClick={() => setDrawerOpen(false)}
                ></div>
            )}

            {/* ---------------- SIDE DRAWER MENU ---------------- */}
            <div
                className={`fixed top-0 right-0 h-full w-72 bg-gray-900 text-white shadow-2xl transform transition-transform duration-300 z-50 ${drawerOpen ? "translate-x-0" : "translate-x-full"
                    }`}
            >
                {/* Drawer Header */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-700">
                    <h2 className="text-xl font-semibold">Menu</h2>
                    <button
                        onClick={() => setDrawerOpen(false)}
                        className="text-gray-400 hover:text-white text-2xl"
                    >
                        ×
                    </button>
                </div>

                {/* Drawer Options */}
                <nav className="flex flex-col p-6 space-y-3">
                    {/* Testing */}
                    <button
                        onClick={() => {
                            setShowTestingModal(true);
                            setDrawerOpen(false);
                        }}
                        className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-md text-left font-medium"
                    >
                        🧪 Testing
                    </button>

                    {/* Change Password */}
                    <button
                        onClick={() => {
                            setShowChangePassword(!showChangePassword);
                            setDrawerOpen(false);
                        }}
                        className="bg-orange-500 hover:bg-orange-600 px-4 py-2 rounded-md text-left font-medium"
                    >
                        🔐 {showChangePassword ? "Close Password Form" : "Change Password"}
                    </button>

                    {/* Logout */}
                    <button
                        onClick={() => {
                            handleLogout();
                            setDrawerOpen(false);
                        }}
                        className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-md text-left font-medium"
                    >
                        🚪 Logout
                    </button>
                </nav>
            </div>

            {showChangePassword && (
                <ChangePassword
                    username={username}
                    role="Developer"
                    onClose={() => setShowChangePassword(false)}
                    onPasswordChangeSuccess={() => {
                        setShowChangePassword(false);
                    }}
                />
            )}

            {/* ✅ Testing Modal Component */}
            {showTestingModal && (
                <TestingModal
                    developerId={developerId}
                    username={username}
                    onClose={() => setShowTestingModal(false)}
                    showCenteredPopup={showCenteredPopup}
                />
            )}

            {/* Error Message */}
            {error && <p className="text-red-500 mb-4 text-center font-medium">{error}</p>}

            {/* ---------------- BACKLOG TASKS ---------------- */}
            {backlogTasks.length > 0 && (
                <div className="bg-gray-800 p-6 rounded-2xl shadow-lg mb-8">
                    <h2 className="text-xl font-semibold mb-4">Backlog Tasks Assigned by Admin</h2>
                    <div className="overflow-x-auto">
                        <table className="min-w-full border border-gray-700">
                            <thead>
                                <tr className="bg-gray-700 text-gray-200">
                                    <th className="py-2 px-4 border border-gray-600">Project</th>
                                    <th className="py-2 px-4 border border-gray-600">Description</th>
                                    <th className="py-2 px-4 border border-gray-600">Status</th>
                                    <th className="py-2 px-4 border border-gray-600">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentBacklogs.map((task) => (
                                    <tr key={task.id} className="text-gray-300 text-sm">
                                        <td className="py-2 px-4 border border-gray-700">{task.project_name}</td>
                                        <td className="py-2 px-4 border border-gray-700">{task.description}</td>
                                        <td className="py-2 px-4 border border-gray-700 font-semibold">
                                            {task.status}
                                        </td>
                                        <td className="py-2 px-4 border border-gray-700">
                                            {task.status === "Pending" && (
                                                <button
                                                    onClick={() => handleAcknowledge(task)}
                                                    className="bg-indigo-600 hover:bg-indigo-700 px-4 py-1 rounded-lg text-sm font-semibold"
                                                >
                                                    Acknowledge
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {/* Pagination for Backlog Tasks */}
                        {totalBacklogPages > 1 && (
                            <div className="flex justify-center items-center mt-4 space-x-2">
                                <button
                                    disabled={currentBacklogPage === 1}
                                    onClick={() => setCurrentBacklogPage(currentBacklogPage - 1)}
                                    className="px-3 py-1 bg-gray-700 rounded-md disabled:opacity-50"
                                >
                                    Prev
                                </button>
                                <span className="text-gray-300">
                                    Page {currentBacklogPage} of {totalBacklogPages}
                                </span>
                                <button
                                    disabled={currentBacklogPage === totalBacklogPages}
                                    onClick={() => setCurrentBacklogPage(currentBacklogPage + 1)}
                                    className="px-3 py-1 bg-gray-700 rounded-md disabled:opacity-50"
                                >
                                    Next
                                </button>
                            </div>
                        )}

                    </div>
                </div>
            )}

            {/* ---------------- TASK FORM ---------------- */}
            <div className="bg-gray-800 p-6 rounded-2xl shadow-lg mb-8">
                <h2 className="text-xl font-semibold mb-4">Daily Task Update</h2>

                {selectedBacklogTask && (
                    <p className="text-yellow-400 font-medium mb-2">
                        Filling task for backlog: <strong>{selectedBacklogTask.project_name}</strong>
                    </p>
                )}

                <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Developer Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Developer Name</label>
                        <input
                            type="text"
                            value={username}
                            readOnly
                            className="w-full px-3 py-2 bg-gray-700 rounded-md text-gray-300 cursor-not-allowed"
                        />
                    </div>

                    {/* Project Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Project Name</label>
                        <input
                            type="text"
                            value={projectName}
                            onChange={(e) => setProjectName(e.target.value)}
                            placeholder="Enter Project Name"
                            required
                            className="w-full px-3 py-2 bg-gray-700 rounded-md text-gray-100 focus:outline-none"
                        />
                    </div>

                    {/* Task Type */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Task Type</label>
                        <select
                            value={taskType}
                            onChange={(e) => setTaskType(e.target.value)}
                            required
                            className="w-full px-3 py-2 bg-gray-700 rounded-md text-gray-100 focus:outline-none"
                        >
                            <option value="" disabled>Select Task Type</option>
                            <option value="Coding">Coding</option>
                            <option value="Research">Research</option>
                            <option value="Support">Support</option>
                        </select>
                    </div>

                    {/* Date */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Date</label>
                        <DatePicker
                            selected={date}
                            onChange={(date) => setDate(date)}
                            dateFormat="yyyy-MM-dd"
                            placeholderText="Select a date"
                            className="w-full px-3 py-2 bg-gray-700 rounded-md text-gray-100"
                            required
                        />
                    </div>

                    {/* Time Slot */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Time</label>
                        <select
                            value={timeSlot}
                            onChange={(e) => setTimeSlot(e.target.value)}
                            required
                            className="w-full px-3 py-2 bg-gray-700 rounded-md text-gray-100"
                        >
                            <option value="" disabled>Select Time Slot</option>
                            <option value="First Half">First Half</option>
                            <option value="Second Half">Second Half</option>
                        </select>
                    </div>

                    {/* Description */}
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Describe your work..."
                            required
                            rows={3}
                            className="w-full px-3 py-2 bg-gray-700 rounded-md text-gray-100 focus:outline-none resize-none"
                        />
                    </div>

                    {/* Document Upload */}
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                            Attach Document (Optional)
                        </label>
                        <input
                            type="file"
                            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                            onChange={(e) => setDocumentFile(e.target.files[0])}
                            className="w-full px-3 py-2 bg-gray-700 rounded-md text-gray-100 focus:outline-none"
                        />
                        {documentFile && (
                            <p className="text-sm text-green-400 mt-1">
                                Selected: {documentFile.name}
                            </p>
                        )}
                    </div>

                    {/* Status */}
                    <div className="md:col-span-2 flex gap-4 items-center">
                        <span className="text-gray-300 font-medium">Status:</span>
                        <button
                            type="button"
                            onClick={() =>
                                setStatus((prev) => (prev === "In Progress" ? "Completed" : "In Progress"))
                            }
                            className={`px-4 py-2 rounded-lg font-semibold transition duration-300 ${status === "Completed"
                                ? "bg-green-600 hover:bg-green-700"
                                : "bg-yellow-600 hover:bg-yellow-700"
                                }`}
                        >
                            {status}
                        </button>
                    </div>

                    {/* Submit */}
                    <div className="md:col-span-2 text-right">
                        <button
                            type="submit"
                            className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-lg font-semibold transition duration-300"
                        >
                            Submit Task
                        </button>
                    </div>
                </form>
            </div>

            {/* ---------------- TASK TABLE ---------------- */}
            <div className="bg-gray-800 p-6 rounded-2xl shadow-lg">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold">Your Task Entries</h2>

                    {/* 🟢 Toggle button for Completed / In Progress */}
                    <button
                        onClick={() => setShowCompleted((prev) => !prev)}
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-lg font-semibold transition duration-300"
                    >
                        {showCompleted ? "Show In-Progress Tasks" : "Show Completed Tasks"}
                    </button>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full border border-gray-700 table-fixed">
                        <thead>
                            <tr className="bg-gray-700 text-gray-200">
                                <th className="py-2 px-4 border border-gray-600 min-w-[150px]">Project Name</th>
                                <th className="py-2 px-4 border border-gray-600 min-w-[130px] whitespace-nowrap">Task Type</th>
                                <th className="py-2 px-4 border border-gray-600 min-w-[140px] whitespace-nowrap text-center">Date</th>
                                <th className="py-2 px-4 border border-gray-600 min-w-[120px] whitespace-nowrap">Time</th>
                                <th className="py-2 px-4 border border-gray-600 min-w-[250px] break-words">Description</th>
                                <th className="py-2 px-4 border border-gray-700 text-left">Document</th>
                                <th className="py-2 px-4 border border-gray-600 min-w-[140px] whitespace-nowrap text-center">Status</th>
                            </tr>
                        </thead>

                        <tbody>
                            {currentTasks.length > 0 ? (
                                currentTasks.map((task) => (
                                    <tr key={task.id} className="text-gray-300 text-sm">
                                        <td className="py-2 px-4 border border-gray-700">{task.project_name || "—"}</td>
                                        <td className="py-2 px-4 border border-gray-700 whitespace-nowrap">{task.task_type}</td>
                                        <td className="py-2 px-4 border border-gray-700 whitespace-nowrap text-center">{task.date}</td>
                                        <td className="py-2 px-4 border border-gray-700 whitespace-nowrap">{task.time_slot}</td>
                                        <td className="py-2 px-4 border border-gray-700 break-words">
                                            <div>{task.description}</div>

                                            {task.admin_updates && task.admin_updates.length > 0 && (
                                                <div className="mt-2 border-t border-gray-600 pt-2">
                                                    <p className="text-sm text-orange-400 font-semibold mb-1">Admin Updates:</p>
                                                    <ul className="space-y-1">
                                                        {task.admin_updates.map((update, idx) => (
                                                            <li key={idx} className="text-xs text-gray-300">
                                                                <span className="text-indigo-400 font-semibold">{update.admin_username}:</span>{" "}
                                                                {update.update_text}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </td>

                                        <td className="py-2 px-4 border border-gray-700 text-center">
                                            {task.document_path ? (
                                                <a
                                                    href={
                                                        (() => {
                                                            const docPath = task.document_path.replace(/\\/g, "/");
                                                            // Ensure URL always starts correctly
                                                            const url = docPath.startsWith("/uploads")
                                                                ? `${BASE_URL}${docPath}`
                                                                : `${BASE_URL}/uploads/${docPath.replace(/^uploads\//, "")}`;
                                                            return url;
                                                        })()
                                                    }
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-400 underline"
                                                >
                                                    View
                                                </a>
                                            ) : (
                                                <button
                                                    className="bg-gray-600 text-gray-300 text-sm px-3 py-1 rounded-md cursor-not-allowed"
                                                    disabled
                                                >
                                                    No File
                                                </button>
                                            )}
                                        </td>


                                        <td className="py-2 px-4 border border-gray-700 text-center whitespace-nowrap">
                                            <button
                                                onClick={() => handleStatusChange(task.id, task.status)}
                                                className={`px-3 py-1 rounded-md font-semibold transition duration-300 ${task.status === "Completed"
                                                    ? "bg-green-700 text-white cursor-not-allowed opacity-70"
                                                    : "bg-yellow-500 hover:bg-yellow-600 text-black"
                                                    }`}
                                                disabled={task.status === "Completed"}
                                            >
                                                {task.status}
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center text-gray-400 py-4">
                                        No {showCompleted ? "completed" : "in-progress"} tasks found.
                                    </td>
                                </tr>
                            )}
                        </tbody>

                    </table>

                    {/* Pagination for Developer Tasks */}
                    {totalTaskPages > 1 && (
                        <div className="flex justify-center items-center mt-4 space-x-2">
                            <button
                                disabled={currentTaskPage === 1}
                                onClick={() => setCurrentTaskPage(currentTaskPage - 1)}
                                className="px-3 py-1 bg-gray-700 rounded-md disabled:opacity-50"
                            >
                                Prev
                            </button>
                            <span className="text-gray-300">
                                Page {currentTaskPage} of {totalTaskPages}
                            </span>
                            <button
                                disabled={currentTaskPage === totalTaskPages}
                                onClick={() => setCurrentTaskPage(currentTaskPage + 1)}
                                className="px-3 py-1 bg-gray-700 rounded-md disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            </div>

        </div>
    );
};

export default DeveloperDashboard;
